import { useEffect } from 'react';

const SITE_NAME = 'AllSEO Conferences';
const SITE_URL = 'https://allseoconferences.com';

const setMeta = (name, content, property) => {
  const attr = property ? 'property' : 'name';
  const key = property || name;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (data) => {
  let el = document.querySelector('script[data-seo-jsonld]');
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-jsonld', 'true');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);
};

const SEO = ({ title, description, canonical, type = 'website', image, jsonLd }) => {
  const fullTitle = title ? title + ' | ' + SITE_NAME : SITE_NAME;
  const desc = description || 'Browse top-rated technical SEO summits, content strategy workshops, and digital marketing networking events.';
  const img = image || SITE_URL + '/logo-light.webp';
  const url = canonical || SITE_URL;

  useEffect(() => {
    document.title = fullTitle;
    setMeta('description', desc);
    setLink('canonical', url);
    setMeta(null, fullTitle, 'og:title');
    setMeta(null, desc, 'og:description');
    setMeta(null, type, 'og:type');
    setMeta(null, url, 'og:url');
    setMeta(null, img, 'og:image');
    setMeta(null, SITE_NAME, 'og:site_name');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', img);
    if (jsonLd) setJsonLd(jsonLd);
  }, [fullTitle, desc, url, type, img, jsonLd]);

  return null;
};

export const conferenceJsonLd = (conf, url) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: conf.title,
  description: conf.description,
  startDate: conf.date,
  eventAttendanceMode: conf.format === 'Online'
    ? 'https://schema.org/OnlineEventAttendanceMode'
    : conf.format === 'Hybrid'
      ? 'https://schema.org/MixedEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
  eventStatus: 'https://schema.org/EventScheduled',
  location: conf.format === 'Online'
    ? { '@type': 'VirtualLocation', url: conf.websiteUrl || url }
    : { '@type': 'Place', name: conf.location, address: { '@type': 'PostalAddress', addressLocality: conf.location } },
  image: conf.coverImage,
  organizer: conf.organizer ? { '@type': 'Organization', name: conf.organizer } : undefined,
  url,
});

export const blogJsonLd = (post, url) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt || post.metaDescription,
  image: post.coverImage,
  datePublished: post.publishedAt,
  author: { '@type': 'Organization', name: SITE_NAME },
  publisher: { '@type': 'Organization', name: SITE_NAME },
  mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  url,
});

export const homeJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: 'The premier directory for SEO conferences and search marketing events worldwide.',
  potentialAction: {
    '@type': 'SearchAction',
    target: SITE_URL + '/?search={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
});

export const conferenceListJsonLd = (conferences) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: conferences.slice(0, 20).map((conf, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: { '@type': 'Event', name: conf.title, startDate: conf.date, url: SITE_URL + '/conferences/' + (conf.slug || conf.id) },
  })),
});

export default SEO;
